import {
  DEFAULT_LEGEND,
  type LegendLabel,
  LINE_COLORS,
  lineColorName,
  normalizeLegendKeys,
  pricePerFtFromInput,
} from '@better-lights/core';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiMessage } from '../api/errors.ts';
import { loadCatalog, saveCatalog } from '../branding/catalog.ts';
import { parsePrice } from '../lib/format.ts';
import { lengthUnit, priceInputText } from '../lib/units.ts';
import { has } from '../plans/features.ts';
import { handleFeatureError, openPaywallFor, useFlags } from '../plans/gate.ts';
import { toast } from '../state/feedback.ts';
import { useSettings } from '../state/settingsStore.ts';
import { Button } from '../ui/Button.tsx';
import { Field } from '../ui/Field.tsx';
import { LockBadge } from '../ui/LockBadge.tsx';
import { Section } from '../ui/Section.tsx';

/**
 * The company's shared price list (org catalog `legendLabels` + `currency`). New projects in the
 * company workspace copy it, so later edits never change old quotes. Admin+ edit; others read.
 */
export function PriceList({ orgId, canEdit }: { orgId: string; canEdit: boolean }) {
  const [labels, setLabels] = useState<Record<string, LegendLabel> | null>(null);
  const [currency, setCurrency] = useState('$');
  const [busy, setBusy] = useState(false);
  const units = useSettings((s) => s.units);
  // Company workspaces are cloud-backed: without a Team plan (no sync) the list can't be shared.
  const shared = has(useFlags(orgId), 'sync');

  useEffect(() => {
    let alive = true;
    void loadCatalog(orgId).then((c) => {
      if (!alive) return;
      const l = c?.doc.legendLabels;
      setLabels(l && Object.keys(l).length ? normalizeLegendKeys(l) : { ...DEFAULT_LEGEND });
      setCurrency(c?.doc.currency ?? '$');
    });
    return () => {
      alive = false;
    };
  }, [orgId]);

  if (!labels) return <p className="t-body text-dim">Loading price list…</p>;
  const set = (hex: string, patch: Partial<LegendLabel>) =>
    setLabels((cur) => ({
      ...cur,
      [hex]: { ...(cur?.[hex] ?? { name: lineColorName(hex), product: '', pricePerFt: null }), ...patch },
    }));

  return (
    <div className="flex flex-col gap-4">
      <Section title="Shared price list" right={shared ? null : <LockBadge flag="shared_catalog" />}>
        {shared ? null : <p className="t-caption text-accent">The shared price list is part of the Team plan.</p>}
        <p className="t-caption text-dim">
          Each line color stands for a product. Reps see these names and prices in new projects;{' '}
          {canEdit ? 'only owners and admins can change them.' : 'ask an admin to change them.'}
        </p>
        <Field
          label="Currency symbol"
          className="w-28"
          maxLength={3}
          disabled={!canEdit}
          value={currency}
          onChange={(e) => setCurrency(e.target.value.slice(0, 3))}
        />
        <div className="flex flex-col gap-2">
          {LINE_COLORS.map(({ hex }) => {
            const l = labels[hex];
            return (
              <div
                key={hex}
                className="grid grid-cols-[6px_1fr] gap-3 rounded-md bg-surface2 p-3"
                data-testid="price-row"
              >
                <span className="rounded-full" style={{ background: hex }} />
                <div className="grid grid-cols-1 gap-2 min-[700px]:grid-cols-[1fr_1fr_130px]">
                  <Field
                    label="Name"
                    disabled={!canEdit}
                    value={l?.name ?? ''}
                    placeholder={lineColorName(hex)}
                    onChange={(e) => set(hex, { name: e.target.value })}
                  />
                  <Field
                    label="Product"
                    disabled={!canEdit}
                    value={l?.product ?? ''}
                    onChange={(e) => set(hex, { product: e.target.value })}
                  />
                  <Field
                    key={`${hex}-${units}`}
                    label="Price"
                    prefix={currency}
                    suffix={`/${lengthUnit(units)}`}
                    inputMode="decimal"
                    disabled={!canEdit}
                    placeholder="none"
                    defaultValue={priceInputText(l?.pricePerFt, units)}
                    onBlur={(e) => {
                      const v = parsePrice(e.target.value);
                      set(hex, { pricePerFt: v == null ? null : pricePerFtFromInput(v, units) });
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Section>
      {canEdit ? (
        <Button
          variant="primary"
          icon={Save}
          className="self-start"
          loading={busy}
          onClick={async () => {
            if (!shared) {
              openPaywallFor('shared_catalog', 'team', orgId);
              return;
            }
            setBusy(true);
            try {
              const clean = Object.fromEntries(
                Object.entries(labels).filter(([, l]) => l.name.trim() || l.product.trim() || l.pricePerFt != null),
              );
              await saveCatalog(orgId, { legendLabels: clean, currency });
              toast('Price list saved', 'success');
            } catch (e) {
              if (!handleFeatureError(e, orgId)) toast(apiMessage(e, 'Could not save the price list.'), 'error');
            } finally {
              setBusy(false);
            }
          }}
        >
          Save price list
        </Button>
      ) : null}
    </div>
  );
}
